import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getAccountDetails,
  getAccountWithActions,
} from "../../../services/services";
import Label from "../../../components/Label";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import { Clear } from "@mui/icons-material";
import HeaderHelpPage from "../../../components/HeaderHelpPage";
import { styles } from "../../../utils/styles";
import PendingDetailsComparison from "../../../components/PendingDetailsComparison";

const ADMIN_USER_FIELDS = [
  { key: "group", label: "Group" },
  {
    key: "department",
    label: "Department",
    render: (value) => value?.department || "N/A",
  },
  {
    key: "oxygen7_entity",
    label: "Entity",
    render: (value) => value?.entity || "N/A",
  },
  { key: "contact_number", label: "Contact Number" },
  {
    key: "salutation",
    label: "Salutation",
    render: (value) => value?.salutation || "N/A",
  },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
];

const AccountDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [accDetails, setAccDetails] = useState();
  const [loaded, setLoaded] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  // Every maker-and-checker view: the pending dialog and the history dialog.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    if (!detailsData && !action) {
      getAccountDetails(id, setAccDetails, setLoaded);
      return;
    } 
    if (!detailsData && action) {
      getAccountWithActions(id, setAccDetails, setLoaded);
      return;
    }

    setAccDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  const handleBack = () => window.close();
  
  const InfoColumn = () => {
    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Group Name</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.group ? accDetails.group.name : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Department</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.department ? accDetails.department.department : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Entity</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.oxygen7_entity?.entity || "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Contact Number</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.contact_number}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Salutation</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.salutation ? accDetails.salutation.salutation : "N/A"}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">First Name</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              md={7}
              sx={{
                overflowWrap: "normal",
                wordBreak: "break-word",
              }}
            >
              {accDetails.first_name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Last Name</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.last_name}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">Email</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.email}
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  }

  const DateColumn = () => {
    const requestedOnLabel = isHistoryView ? "Requested on" : "Created on";
    const requestedByLabel = isHistoryView ? "Requested by" : "Created by";
    const reviewedOnLabel = isHistoryView ? "Reviewed on" : "Approved on";
    const reviewedByLabel = isHistoryView ? "Reviewed by" : "Approved by";
    const formatTimestamp = isHistoryView ? parseDateTime : parseDate;

    return (
      <Grid item xs={6} md={6}>
        <Box
          sx={{
            height: "100%",
            p: 3,
            border: "1px solid #999",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">
                {action === "true" ? "Approved" : "Active Now"}
              </Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {action === "true" ? (
                <Label
                  variant="ghost"
                  color={accDetails.is_approved ? "success" : "error"}
                >
                  {accDetails.is_approved ? "Yes" : "No"}
                </Label>
              ) : (
                <Label
                  variant="ghost"
                  color={accDetails.is_active ? "success" : "error"}
                >
                  {accDetails.is_active ? "YES" : "NO"}
                </Label>
              )}
            </Grid>
            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedOnLabel}</Typography>
            </Grid>
            <Grid item xs={4} md={7}>
              {accDetails.create_at ? formatTimestamp(accDetails.create_at) : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{requestedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.created_by ? accDetails.created_by.email : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedOnLabel}</Typography>
            </Grid>
            <Grid item xs={4} md={7}>
              {accDetails.approved_at ? formatTimestamp(accDetails.approved_at) : "N/A"}
            </Grid>

            <Grid item xs={6} md={5}>
              <Typography variant="subtitle1">{reviewedByLabel}</Typography>
            </Grid>
            <Grid item xs={6} md={7}>
              {accDetails.approved_by ? accDetails.approved_by.email : "N/A"}
            </Grid>

            {!action && (
              <>
                <Grid item xs={6} md={5}>
                  <Typography variant="subtitle1">Updated by</Typography>
                </Grid>
                <Grid item xs={6} md={7}>
                  {accDetails.updated_by ? accDetails.updated_by.email : "N/A"}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Grid>
    );
  };

  return (
    <Container>
      {!detailsData && !itemId && (
        <Stack
          direction="row"
          sx={{
            ...styles.flexBetween,
            mb: 2,
          }}
        >
          <HeaderHelpPage title="Account Details" variant={mobile ? "h5" : "h3"} />

          <Button
            variant="outlined"
            onClick={handleBack}
            endIcon={<Clear />}
          >
            Back
          </Button>
        </Stack>
      )}
      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: {accDetails.action}
        </Typography>
      )}
      {loaded ? (
        <Card sx={{ p: 3 }}>
          <Grid
            container
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 2, md: 2 }}
            sx={{ mt: 5 }}
          >
            {isCheckerView && !accDetails.original_unavailable ? (
              <>
                <PendingDetailsComparison
                  data={accDetails}
                  original={accDetails.original}
                  fields={ADMIN_USER_FIELDS}
                  greyBeforeOnUnsuspend
                />
                <DateColumn />
              </>
            ) : (
              <>
                {action && <DateColumn />}
                <InfoColumn />
                {!action && <DateColumn />}
              </>
            )}
          </Grid>
          {accDetails.reject_message && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {accDetails.reject_message}
                </Typography>
              </Typography>
            </>
          )}
          {accDetails.action_message && (
            <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
              Action message:
              <Typography sx={{ ml: 1 }}>
                {" "}
                {accDetails.action_message}
              </Typography>
            </Typography>
          )}
        </Card>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Container>
  );
};

export default AccountDetails;
